import { Component, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';

import { AuthService, isConnectivityFailure } from '@lib/auth/data-access';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { BroadcastService } from '@lib/shared/data-access';
import { BroadcastChannelType } from '@lib/shared/models';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { PopoverModule } from 'primeng/popover';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-inactivity-feature-inactivity',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    DialogModule,
    CheckboxModule,
    TooltipModule,
    PopoverModule
],
  templateUrl: './inactivity.component.html',
  styleUrl: './inactivity.component.scss',
})
export class InactivityComponent implements OnInit, OnDestroy {

  public showWarningSignal = signal(false);

  private inactivityTimeout: any;
  private lastActivityTimestamp: number = Date.now();
  private sessionEndTimestamp: number = Date.now();

  public authService = inject(AuthService);
  private broadcastService = inject(BroadcastService);
    
  private interval: NodeJS.Timeout | null = null;
  private durationSeconds: number = 3 * 60;
  private intervalTime: number = 1000; // Update every second

  public formattedTimeSignal = signal<string>('3:00 remaining');
  public extendSessionButtonDisabled = signal<boolean>(false);

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.setInactivityTimeout();
    this.broadcastService.addMessageListener(BroadcastChannelType.UserActivity, () => {
      this.showWarningSignal.set(false);
      this.setInactivityTimeout();
    });
  }

  startCountdown() {
    this.interval = setInterval(() => {

      if (this.durationSeconds > 0) {
        this.durationSeconds--;
      }
      const minutes = Math.floor(this.durationSeconds / 60);
      const seconds = this.durationSeconds % 60;
      
      this.formattedTimeSignal.set(`${minutes}:${seconds < 10 ? '0' : ''}${seconds} remaining`);

      if (minutes <= 0 && seconds <= 0) {
        this.extendSessionButtonDisabled.set(true);        
        if (this.interval) {
          clearInterval(this.interval);
          this.interval = null;
        }

        this.onLogoutButtonClick();
      }
    }, this.intervalTime);
  }

  setInactivityTimeout(): void {

    if (this.inactivityTimeout) {
      clearTimeout(this.inactivityTimeout);
    }

    this.inactivityTimeout = setTimeout(() => {
      if (!this.authService.isAuthenticatedComputedSignal()) return;
      
      const lastActivity = window.localStorage.getItem('lastActivity');
      const sessionEnd = window.localStorage.getItem('sessionEnd');

      if (sessionEnd) {
        this.sessionEndTimestamp = new Date(sessionEnd).getTime();
        const now = new Date().getTime();
        
        const timeDifferenceInMinutes = (now - this.sessionEndTimestamp) / (1000 * 60); // Convert milliseconds to minutes
        if (timeDifferenceInMinutes >= 1) {
          this.onLogoutButtonClick();
          return;
        }
      }

      if (lastActivity) {
        const lastActivityDate = new Date(lastActivity);
        const currentDate = new Date();
        const lastActivityDateDiff = currentDate.getTime() - lastActivityDate.getTime();

        // check if inactivity is greater than 5 minutes
        //
        // NOT WHILE OFFLINE. A refresh cannot reach Auth0, so it never succeeded, the flag below was never cleared, and
        // every tab asked again every 30 seconds — a failed `/oauth/token` loop for as long as the device stayed offline.
        // The flag stays set, so the refresh happens on the first check after the connection is back.
        if (lastActivityDateDiff >= 5 * 60 * 1000 && window.localStorage.getItem('refreshAllTokensForActivity') === 'true' && navigator.onLine) {
          const prevLastActivity = lastActivity;

          // refresh all tokens so they align in time
          this.refreshTokens().then((result) => {
            if (result) {
              window.localStorage.setItem('refreshAllTokensForActivity', 'false');
            }

            window.localStorage.setItem('lastActivity', prevLastActivity);
          });
        }

        this.lastActivityTimestamp = new Date(lastActivity).getTime();
        const now = new Date().getTime();
        
        const timeDifferenceInMinutes = (now - this.lastActivityTimestamp) / (1000 * 60); // Convert milliseconds to minutes
        
        if (this.authService.isSessionTimeoutOverrideComputedSignal() && timeDifferenceInMinutes >= 6) {
          //Every 2 minutes, extend the session
          this.onExtendSessionButtonClick();
          return;
        }

        if (timeDifferenceInMinutes >= 26 && timeDifferenceInMinutes < 30) {
          // Between 26 and 30 minutes, do something
          this.showWarningSignal.set(true);

          if (this.interval === null) {
            this.startCountdown();
          }

          this.setInactivityTimeout();
        } else if (timeDifferenceInMinutes >= 30) {
          // More than 30 minutes, log the user out
          this.onLogoutButtonClick();
        }
        else {
          this.setInactivityTimeout();
        }
      } else {
        this.setInactivityTimeout();
      }
    }, 30000);  //Check every 30 seconds
  }  

  refreshTokens(): Promise<boolean> {
    //Let other browser tabs know that the token has been refreshed
    this.broadcastService.postMessage(BroadcastChannelType.TokenRefresh, 'Token refresh');

    //Let other browser tabs know that the user has been active and the warning dialog should be hidden
    this.broadcastService.postMessage(BroadcastChannelType.UserActivity, 'User activity');

    return new Promise<boolean>((resolve) => {
      this.authService.extendSession()
        .subscribe({
          next: (result) => {
            resolve(result);
          },
          // A failed refresh leaves the flag set, so the next check tries again rather than waiting forever.
          error: () => resolve(false),
        })
    });
  }

  // Extend session by resetting the inactivity timer and refreshing tokens
  onExtendSessionButtonClick(): void {
    this.showWarningSignal.set(false);  // Hide the warning dialog

    const now = new Date();
    now.setSeconds(now.getSeconds() - 10);
    const isoString = now.toISOString();
    window.localStorage.setItem('lastActivity', isoString);

    // OFFLINE, THE EXTENSION IS LOCAL ONLY: the timer reset below. A token refresh cannot reach Auth0, and its failure
    // used to reach the error handler at the end of this method and LOG THE OFFICER OUT for being offline. The first
    // request made once the connection is back refreshes the token as usual.
    const online = navigator.onLine;

    //Let other browser tabs know that the token has been refreshed
    if (online) {
      this.broadcastService.postMessage(BroadcastChannelType.TokenRefresh, 'Token refresh');
    }

    //Let other browser tabs know that the user has been active and the warning dialog should be hidden
    this.broadcastService.postMessage(BroadcastChannelType.UserActivity, 'User activity');

    this.formattedTimeSignal.set('3:00 remaining');
    this.durationSeconds = 3 * 60;
    
    if (this.interval) clearInterval(this.interval);
    this.interval = null;

    this.setInactivityTimeout();

    if (!online) {
      return;
    }

    // Explicitly refresh tokens to extend the session
    this.authService.extendSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        error: (error: any) => {
          console.error('Error extending session:', error);

          // Auth0 could not be REACHED (a link with no backhaul reports online): the local extension above stands, and
          // the next request made with a working connection refreshes the token. Not a reason to log anyone out.
          if (isConnectivityFailure(error)) {
            return;
          }

          if (error.error_description == 'Login required') {
            this.authService.logout();
          }
          this.onLogoutButtonClick();
        }
      });
  }

  onLogoutButtonClick() {
    this.showWarningSignal.set(false);

    this.authService.logout();  // Log out via Auth0

    //Let other browser tabs know that the user has logged out
    this.broadcastService.postMessage(BroadcastChannelType.Logout, 'Logout');
  }

  ngOnDestroy(): void {
    if (this.inactivityTimeout) clearTimeout(this.inactivityTimeout);
    if (this.interval) clearInterval(this.interval);
  }
}
